import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { MoreVertical, Hourglass, MessageCircleQuestion, RotateCcw } from 'lucide-react';
import { ConfirmDialog, type ConfirmTone } from '@/components/ui/confirm-dialog';
import { reclassifyTicket } from '@/hooks/useAddressValidation';
import { reportTicketReopen } from '@/lib/reopenToast';

// Discreet "⋯" next to the status pill (user ask 2026-07-31, case UT-2026-000004): a ticket handled
// OUTSIDE the app (Build question asked directly in Uniticket before AV existed) sits in the Pending
// queue with no waiting marker. Three LOCAL moves — nothing is posted to Uniticket; the sync's reply
// detection then brings the ticket back to the queue exactly like a real hand-off/question.
// N40 — seule exception : ranger « en attente » un dossier dont le ticket est résolu ou fermé le rouvre dans
// Uniticket (sinon il sortirait de toutes les vues de travail), et le menu dit ce qu'il en a été.

type Target = 'WAITING_BUILD' | 'WAITING_OPERATOR' | 'PENDING';

const ITEM: Record<Target, { icon: typeof Hourglass; labelKey: string; confirmKey: string; doneKey: string; tone: ConfirmTone }> = {
  WAITING_BUILD: { icon: Hourglass, labelKey: 'waitingBuild', confirmKey: 'confirmBuild', doneKey: 'doneBuild', tone: 'sky' },
  WAITING_OPERATOR: { icon: MessageCircleQuestion, labelKey: 'waitingOperator', confirmKey: 'confirmOperator', doneKey: 'doneOperator', tone: 'brand' },
  PENDING: { icon: RotateCcw, labelKey: 'requeue', confirmKey: 'confirmRequeue', doneKey: 'doneRequeue', tone: 'amber' },
};

export function TicketReclassifyMenu({
  ticketId,
  ticketNumber,
  buildParked,
  questionOpen,
  disabled = false,
  onDone,
}: {
  ticketId: string;
  /** Nommé dans le message de réouverture (N40). */
  ticketNumber: string;
  /** Already waiting on Build (row WAITING_BUILD, or ticket buildHandedOffAt without a reply). */
  buildParked: boolean;
  /** Operator question already open (questionAskedAt without a reply). */
  questionOpen: boolean;
  /** Lock held by someone else — the menu opens but every move is disabled. */
  disabled?: boolean;
  onDone: () => void;
}) {
  const { t } = useTranslation(['tickets']);
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirm, setConfirm] = useState<Target | null>(null);
  const [busy, setBusy] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Close the dropdown on outside-click (same dep-free pattern as AddressManagementMenu).
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [menuOpen]);

  const parked = buildParked || questionOpen;
  const itemDisabled: Record<Target, boolean> = {
    WAITING_BUILD: disabled || buildParked,
    WAITING_OPERATOR: disabled || questionOpen,
    PENDING: disabled || !parked,
  };

  async function run(target: Target) {
    setBusy(true);
    try {
      const res = await reclassifyTicket(ticketId, target);
      toast.success(t(`tickets:validation.reclassify.${ITEM[target].doneKey}`));
      // N40 — ranger « en attente » un dossier dont le ticket était résolu ou fermé : dire s'il est reparti.
      reportTicketReopen(res.data.uniticketReopen, ticketNumber, t);
      setConfirm(null);
      onDone();
    } catch (err) {
      toast.error((err as Error)?.message || t('tickets:validation.reclassify.error'));
    } finally {
      setBusy(false);
    }
  }

  const ConfirmIcon = confirm ? ITEM[confirm].icon : Hourglass;

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setMenuOpen((o) => !o)}
        title={t('tickets:validation.reclassify.button')}
        aria-label={t('tickets:validation.reclassify.button')}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
      >
        <MoreVertical className="h-4 w-4" />
      </button>

      {menuOpen && (
        <div role="menu" className="absolute right-0 z-20 mt-1 w-72 rounded-xl border border-slate-200 bg-white p-1 shadow-lg">
          {(Object.keys(ITEM) as Target[]).map((target) => {
            const { icon: Icon, labelKey } = ITEM[target];
            return (
              <button
                key={target}
                type="button"
                role="menuitem"
                disabled={itemDisabled[target]}
                onClick={() => { setMenuOpen(false); setConfirm(target); }}
                className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-slate-700 hover:bg-brand-rose/[0.06] hover:text-slate-900 disabled:pointer-events-none disabled:opacity-40"
              >
                <Icon className="h-4 w-4 text-brand-rose" />
                {t(`tickets:validation.reclassify.${labelKey}`)}
              </button>
            );
          })}
          <p className="px-3 pb-1.5 pt-1 text-[10px] text-slate-400">{t('tickets:validation.reclassify.hint')}</p>
        </div>
      )}

      <ConfirmDialog
        open={confirm !== null}
        tone={confirm ? ITEM[confirm].tone : 'brand'}
        icon={<ConfirmIcon className="h-5 w-5" />}
        title={t('tickets:validation.reclassify.confirmTitle')}
        message={confirm ? t(`tickets:validation.reclassify.${ITEM[confirm].confirmKey}`) : ''}
        confirmLabel={t('tickets:validation.reclassify.ok')}
        cancelLabel={t('tickets:validation.reclassify.cancel')}
        busy={busy}
        onCancel={() => { if (!busy) setConfirm(null); }}
        onConfirm={() => { if (confirm) void run(confirm); }}
      />
    </div>
  );
}
